import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Table,
  Button,
  Modal,
  TextArea,
  Form,
} from 'semantic-ui-react';
import moment from 'moment';
import { OldCardTransactionWithdrawInfo } from '@types';
import LoadingIndicator from '@component/LoadingIndicator/LoadingIndicator';
import { apis } from '@api/index';
import WithdrawOldCardCashHeader from './WithdrawOldCardCashHeader';
import DateSelect from '../DateSelect';
import { DateInfo } from '../DateSelect/DateSelect';
import TransactionTransferOldCardCashExcel from './TransactionTransferOldCardCashExcel';
import { defaultWithdrawAnswer } from '../User.constant';
import TransactionTransferOldCardCashRow from './TransactionTransferOldCardCashRow';
import SearchWithdraw, { WithdrawSearchType } from '../Withdraw/SearchWithdraw';
import DurationPagination from '@component/DurationPagination/DurationPagination';

const TransactionTransferOldCardCash = () => {
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchType, setSearchType] =
    useState<WithdrawSearchType>('accountName');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const [withdraws, setWithdraws] = useState<OldCardTransactionWithdrawInfo[]>(
    [],
  );
  const [activeTab, setActiveTab] = useState<'NOT_COMPLETED' | 'COMPLETED'>(
    'NOT_COMPLETED',
  );
  const [dateInfo, setDateInfo] = useState<DateInfo>({
    startDate: moment().subtract(1, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [checkedAll, setCheckedAll] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWithdraw, setSelectedWithdraw] =
    useState<OldCardTransactionWithdrawInfo>();

  const filteredWithdraws = useMemo(() => {
    const isCompleted = activeTab === 'COMPLETED';
    return withdraws
      .filter((withdraw) => withdraw.isCompleted === isCompleted)
      .filter((withdraw) => {
        if (!searchKeyword) return true;
        if (searchType === 'userId') {
          return String(withdraw.userId).includes(searchKeyword);
        }
        if (searchType === 'accountNo') {
          return withdraw.accountNo.includes(searchKeyword);
        }
        if (searchType === 'bankName') {
          return withdraw.bankName.includes(searchKeyword);
        }
        return withdraw.accountName.includes(searchKeyword);
      });
  }, [activeTab, withdraws, searchKeyword, searchType]);

  const handleClickTab = (tab: 'NOT_COMPLETED' | 'COMPLETED') => {
    setActiveTab(tab);
    setSearchInputValue('');
    setSearchKeyword('');
    setCurrentPage(1);
  };

  const handleChangeDate = (key: keyof DateInfo, value: any) => {
    setDateInfo((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const getWithdraws = async () => {
    LoadingIndicator.show();
    const { transactions: _withdraws } =
      await apis.common.getOldCardTransactions({
        gteCreatedAt: moment(dateInfo.startDate)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .add(-9, 'h')
          .format('YYYY-MM-DD HH:mm:ss'),
        ltCreatedAt: moment(dateInfo.endDate)
          .add(1, 'd')
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0)
          .add(-9, 'h')
          .format('YYYY-MM-DD HH:mm:ss'),
      });
    setWithdraws(_withdraws);
    LoadingIndicator.hide();
  };

  useEffect(() => {
    getWithdraws();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeCheckAll = () => {
    if (checkedAll) {
      setCheckedAll(false);
      setWithdraws((prev) => {
        return prev.map((withdraw) => ({ ...withdraw, isChecked: false }));
      });
    } else {
      setWithdraws((prev) => {
        return prev.map((withdraw) => {
          if (withdraw.answer) return { ...withdraw, isChecked: false };
          return { ...withdraw, isChecked: true };
        });
      });
      setCheckedAll(true);
    }
  };

  const handleChangeCheckbox = useCallback(
    (id: number) => {
      setWithdraws((prev) => {
        return prev.map((withdraw) => {
          if (withdraw.id === id) {
            return { ...withdraw, isChecked: !withdraw.isChecked };
          }
          return withdraw;
        });
      });
    },
    [setWithdraws],
  );

  const showModal = useCallback((withdraw: OldCardTransactionWithdrawInfo) => {
    setModalOpen(true);
    setSelectedWithdraw({
      ...withdraw,
      answer: withdraw.answer ?? defaultWithdrawAnswer,
    });
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleWithdrawChange = (
    key: keyof OldCardTransactionWithdrawInfo,
    value: any,
  ) => {
    if (!selectedWithdraw) return;
    setSelectedWithdraw((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
  };

  const handleChangeWithdrawIsCompleted = useCallback(
    async (id: number) => {
      await apis.common.updateOldCardTransactionTransferIsCompleted({
        ids: [id],
      });
      setWithdraws((prev) => {
        return prev.map((withdraw) => {
          if (withdraw.id === id)
            return { ...withdraw, isCompleted: !withdraw.isCompleted };
          return withdraw;
        });
      });
    },
    [setWithdraws],
  );

  const completeWithdrawOldCardCashes = async () => {
    LoadingIndicator.show();
    const ids: number[] = [];
    withdraws.forEach((withdraw) => {
      if (withdraw.isChecked) ids.push(withdraw.id);
    });
    await apis.common.updateOldCardTransactionTransferIsCompleted({
      ids,
    });
    setWithdraws((prev) => {
      return prev.map((withdraw) => ({
        ...withdraw,
        isCompleted:
          withdraw.isChecked === undefined ? false : withdraw.isChecked,
        isChecked: false,
      }));
    });
    setCheckedAll(false);

    LoadingIndicator.hide();
    alert('출금처리가 완료되었습니다.');
  };

  const submitWithdrawAnswer = async () => {
    if (!selectedWithdraw || !selectedWithdraw.answer) return;
    await apis.common.answerOldCardTransactions({
      ids: [selectedWithdraw.id],
      answer: selectedWithdraw.answer,
    });
    setWithdraws((prev) => {
      return prev.map((withdraw) => {
        if (withdraw.id === selectedWithdraw.id)
          return { ...withdraw, answer: selectedWithdraw.answer };
        return withdraw;
      });
    });
    closeModal();
  };

  const handleChangeSearchType = useCallback(
    (type: WithdrawSearchType) => {
      setSearchType(type);
    },
    [setSearchType],
  );

  const searchUser = useCallback(async () => {
    setSearchKeyword(searchInputValue);
  }, [searchInputValue]);

  return (
    <>
      <WithdrawOldCardCashHeader
        activeTab={activeTab}
        handleClick={handleClickTab}
      />
      <DateSelect
        handleChangeDate={handleChangeDate}
        fetch={getWithdraws}
        defaultStartDate={dateInfo.startDate}
      />
      <TransactionTransferOldCardCashExcel withdraws={filteredWithdraws} />
      <Button
        size="tiny"
        onClick={completeWithdrawOldCardCashes}
        content="처리완료"
      />

      <SearchWithdraw
        searchText={searchInputValue}
        searchUser={searchUser}
        setSearchKeyword={setSearchInputValue}
        handleChangeSearchType={handleChangeSearchType}
      />

      <Table basic="very" size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <Checkbox checked={checkedAll} onChange={handleChangeCheckAll} />
            </Table.HeaderCell>
            <Table.HeaderCell>거래id</Table.HeaderCell>
            <Table.HeaderCell>유저id</Table.HeaderCell>
            <Table.HeaderCell>요청시기</Table.HeaderCell>
            <Table.HeaderCell>은행</Table.HeaderCell>
            <Table.HeaderCell>계좌번호</Table.HeaderCell>
            <Table.HeaderCell>결제금액</Table.HeaderCell>
            <Table.HeaderCell>결제일</Table.HeaderCell>
            <Table.HeaderCell>금액</Table.HeaderCell>
            <Table.HeaderCell>예금주</Table.HeaderCell>
            <Table.HeaderCell>답변</Table.HeaderCell>
            <Table.HeaderCell>처리여부</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {filteredWithdraws
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((wd) => (
              <TransactionTransferOldCardCashRow
                wd={wd}
                handleChangeCheckbox={handleChangeCheckbox}
                showModal={showModal}
                handleChangeWithdrawIsCompleted={
                  handleChangeWithdrawIsCompleted
                }
              />
            ))}
        </Table.Body>
      </Table>
      <DurationPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={Math.floor(filteredWithdraws.length / pageSize) + 1}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
      <Modal size="tiny" open={modalOpen} onClose={closeModal}>
        <Modal.Header>계좌이체 답변</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <TextArea
                fluid
                name="answer"
                value={selectedWithdraw?.answer}
                onChange={(e) => handleWithdrawChange('answer', e.target.value)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="blue" content="제출" onClick={submitWithdrawAnswer} />
          <Button color="black" content="닫기" onClick={closeModal} />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default TransactionTransferOldCardCash;
